import { Controller } from "@hotwired/stimulus"
import * as pdfjsLib from "pdfjs-dist"

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

const ZOOM_STEP     = 0.10
const ZOOM_MIN      = 0.10
const ZOOM_MAX      = 4.0
const DRAG_THRESHOLD = 6   // px — below this a pointer-down+up is a click

export default class extends Controller {
  static targets = [
    "canvas", "image",
    "pageInfo", "prevPage", "nextPage",
    "xInput", "yInput", "pageInput",
    "pendingPin",
    "pinModeBtn",     // the "Drop pin" button in the toolbar
    "panModeBtn",     // the "Pan" button in the toolbar
    "viewport",       // outer overflow:hidden element — receives pointer events
    "zoomContainer",  // inner element that receives CSS transform
    "zoomDisplay",
  ]
  static values = {
    floorplanUrl: String,
    contentType:  String,
    picker:       Boolean,          // true = pin-placement mode enabled
    currentPage:  { type: Number, default: 1 },
    totalPages:   { type: Number, default: 1 },
    zoom:         { type: Number, default: 1 },
    // Pages of the PDF this floor owns (TASK-85). Empty = all pages.
    allowedPages: { type: Array, default: [] },
    translateX:   { type: Number, default: 0 },
    translateY:   { type: Number, default: 0 },
  }

  // ── Private state ─────────────────────────────────────────────────────────
  #dragging     = false
  #dragStartX   = 0
  #dragStartY   = 0
  #dragStartTx  = 0
  #dragStartTy  = 0
  #dragDistance = 0
  #pinMode      = false   // true = next click drops a pin; false = pan only
  #naturalWidth  = 0
  #naturalHeight = 0

  connect() {
    if (this.isPdf) {
      this.loadPdf()
    } else {
      this.#fitImageOnLoad()
    }
    this.filterPinsByPage()
    // Restore existing pin position when re-opening picker
    if (this.pickerValue) this.#showExistingPin()
  }

  disconnect() {
    this.pdfDoc = null
  }

  get isPdf() {
    return this.contentTypeValue === "application/pdf"
  }

  // ── PDF rendering ──────────────────────────────────────────────────────────
  // Canvas is always rendered at native resolution (scale = devicePixelRatio).
  // Zooming is done entirely via CSS transform, not by re-rendering.

  async loadPdf() {
    if (!this.floorplanUrlValue) return
    try {
      const task = pdfjsLib.getDocument(this.floorplanUrlValue)
      this.pdfDoc = await task.promise
      this.totalPagesValue = this.pdfDoc.numPages
      // Land inside the floor's own pages: keep a valid restored page (the
      // saved pin's), otherwise start on the first owned page.
      if (!this.pageList().includes(this.currentPageValue)) {
        this.currentPageValue = this.pageList()[0]
      }

      const firstPage       = await this.pdfDoc.getPage(this.currentPageValue)
      const naturalViewport = firstPage.getViewport({ scale: 1 })
      this.#naturalWidth    = naturalViewport.width
      this.#naturalHeight   = naturalViewport.height

      await this.renderPage(this.currentPageValue)
      this.updatePageControls()

      await this.#nextFrame()   // let modal paint before measuring
      this.#applyFitAndCenter()
    } catch (err) {
      console.error("[floorplan-viewer] PDF load failed:", err)
    }
  }

  async renderPage(pageNumber) {
    if (!this.pdfDoc || !this.hasCanvasTarget) return
    const page   = await this.pdfDoc.getPage(pageNumber)
    const canvas = this.canvasTarget
    const ctx    = canvas.getContext("2d")
    const dpr    = window.devicePixelRatio || 1

    const viewport = page.getViewport({ scale: dpr })
    canvas.width        = Math.round(viewport.width)
    canvas.height       = Math.round(viewport.height)
    canvas.style.width  = `${Math.round(viewport.width  / dpr)}px`
    canvas.style.height = `${Math.round(viewport.height / dpr)}px`

    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    await page.render({ canvasContext: ctx, viewport }).promise
    this.filterPinsByPage()
  }

  // ── Image fit-on-load ──────────────────────────────────────────────────────

  #fitImageOnLoad() {
    if (!this.hasImageTarget) return
    const img = this.imageTarget
    const run = async () => {
      this.#naturalWidth  = img.naturalWidth
      this.#naturalHeight = img.naturalHeight
      await this.#nextFrame()
      this.#applyFitAndCenter()
    }
    img.complete && img.naturalWidth ? run() : img.addEventListener("load", run, { once: true })
  }

  // ── Fit + center on open ───────────────────────────────────────────────────

  #applyFitAndCenter() {
    this.zoomValue = this.#fitZoom()
    this.#updateZoomDisplay()

    if (this.hasViewportTarget && this.#naturalWidth) {
      const vp = this.viewportTarget
      this.translateXValue = Math.max(0, (vp.clientWidth  - this.#naturalWidth  * this.zoomValue) / 2)
      this.translateYValue = Math.max(0, (vp.clientHeight - this.#naturalHeight * this.zoomValue) / 2)
    }
    this.applyTransform()
  }

  #fitZoom() {
    if (!this.hasViewportTarget || !this.#naturalWidth) return 1
    const vpW = this.viewportTarget.clientWidth
    const vpH = this.viewportTarget.clientHeight
    if (!vpW || !vpH) return 1
    let zoom = vpW / this.#naturalWidth
    if (this.#naturalHeight * zoom > vpH) zoom = vpH / this.#naturalHeight
    return Math.min(Math.max(+zoom.toFixed(3), ZOOM_MIN), ZOOM_MAX)
  }

  // ── Page navigation ────────────────────────────────────────────────────────

  // The navigable pages: the floor's owned pages (clamped to the document)
  // or every page when unrestricted (TASK-85).
  pageList() {
    const all = Array.from({ length: this.totalPagesValue }, (_, index) => index + 1)
    const allowed = (this.allowedPagesValue || [])
      .map(page => parseInt(page, 10))
      .filter(page => page >= 1 && page <= this.totalPagesValue)
      .sort((a, b) => a - b)
    return allowed.length ? allowed : all
  }

  async prevPage() {
    const pages = this.pageList()
    const index = pages.indexOf(this.currentPageValue)
    if (index <= 0) return
    this.currentPageValue = pages[index - 1]
    await this.renderPage(this.currentPageValue)
    this.updatePageControls()
  }

  async nextPage() {
    const pages = this.pageList()
    const index = pages.indexOf(this.currentPageValue)
    if (index === -1 || index >= pages.length - 1) return
    this.currentPageValue = pages[index + 1]
    await this.renderPage(this.currentPageValue)
    this.updatePageControls()
  }

  updatePageControls() {
    const pages = this.pageList()
    const index = pages.indexOf(this.currentPageValue)
    if (this.hasPageInfoTarget) {
      // Actual PDF page number, so it matches the pins' "pg N" labels; the
      // parenthetical counts within the floor's own pages when restricted.
      this.pageInfoTarget.textContent = pages.length === this.totalPagesValue
        ? `Page ${this.currentPageValue} of ${this.totalPagesValue}`
        : `Page ${this.currentPageValue} (${index + 1} of ${pages.length})`
    }
    if (this.hasPrevPageTarget)  this.prevPageTarget.disabled = index <= 0
    if (this.hasNextPageTarget)  this.nextPageTarget.disabled = index === -1 || index >= pages.length - 1
    if (this.hasPageInputTarget) this.pageInputTarget.value = this.currentPageValue
  }

  // ── Zoom ───────────────────────────────────────────────────────────────────

  zoomIn() {
    this.zoomValue = Math.min(+(this.zoomValue + ZOOM_STEP).toFixed(2), ZOOM_MAX)
    this.#updateZoomDisplay()
    this.applyTransform()
  }

  zoomOut() {
    this.zoomValue = Math.max(+(this.zoomValue - ZOOM_STEP).toFixed(2), ZOOM_MIN)
    this.#updateZoomDisplay()
    this.applyTransform()
  }

  #updateZoomDisplay() {
    if (this.hasZoomDisplayTarget) {
      this.zoomDisplayTarget.textContent = `${Math.round(this.zoomValue * 100)}%`
    }
  }

  // ── Pan mode / Pin mode toggle ─────────────────────────────────────────────

  enterPanMode() {
    this.#pinMode = false
    this.#updateModeButtons()
    this.#setCursor("grab")
  }

  enterPinMode() {
    this.#pinMode = true
    this.#updateModeButtons()
    this.#setCursor("crosshair")
  }

  #updateModeButtons() {
    if (this.hasPanModeBtnTarget) {
      this.panModeBtnTarget.dataset.active = this.#pinMode ? "false" : "true"
    }
    if (this.hasPinModeBtnTarget) {
      this.pinModeBtnTarget.dataset.active = this.#pinMode ? "true" : "false"
    }
  }

  #setCursor(style) {
    if (this.hasViewportTarget) this.viewportTarget.style.cursor = style
  }

  // ── Pan / drag ─────────────────────────────────────────────────────────────

  startDrag(event) {
    if (event.button !== undefined && event.button !== 0) return
    this.#dragging     = true
    this.#dragStartX   = event.clientX
    this.#dragStartY   = event.clientY
    this.#dragStartTx  = this.translateXValue
    this.#dragStartTy  = this.translateYValue
    this.#dragDistance = 0

    if (this.hasViewportTarget) {
      this.viewportTarget.style.cursor = "grabbing"
      this.viewportTarget.setPointerCapture(event.pointerId)
    }
    event.preventDefault()
  }

  drag(event) {
    if (!this.#dragging) return
    const dx = event.clientX - this.#dragStartX
    const dy = event.clientY - this.#dragStartY
    this.#dragDistance   = Math.sqrt(dx * dx + dy * dy)
    this.translateXValue = this.#dragStartTx + dx
    this.translateYValue = this.#dragStartTy + dy
    this.applyTransform()
  }

  endDrag(event) {
    if (!this.#dragging) return
    this.#dragging = false

    // If the pointer barely moved AND we're in pin mode → drop a pin
    if (this.#pinMode && this.#dragDistance < DRAG_THRESHOLD) {
      this.#dropPin(event)
      this.enterPanMode()   // auto-return to pan after placing
    } else {
      // Restore appropriate cursor for current mode
      this.#setCursor(this.#pinMode ? "crosshair" : "grab")
    }
  }

  // ── Transform ─────────────────────────────────────────────────────────────

  applyTransform() {
    if (!this.hasZoomContainerTarget) return
    const el = this.zoomContainerTarget
    el.style.transform       = `translate(${this.translateXValue}px, ${this.translateYValue}px) scale(${this.zoomValue})`
    el.style.transformOrigin = "0 0"
  }

  // ── Existing pins ──────────────────────────────────────────────────────────

  filterPinsByPage() {
    this.element.querySelectorAll("[data-floorplan-pin]").forEach(pin => {
      const pinPage = parseInt(pin.dataset.floorplanPinPage || "1", 10)
      pin.style.display = pinPage === this.currentPageValue ? "flex" : "none"
      this.#syncPendingPinVisibility()
  })
    this.#syncPendingPinVisibility()
  }

  // ── Pin placement ──────────────────────────────────────────────────────────

  #dropPin(event) {
    const target = this.hasCanvasTarget ? this.canvasTarget : (this.hasImageTarget ? this.imageTarget : null)
    if (!target) return

    const rect     = target.getBoundingClientRect()
    const xPercent = ((event.clientX - rect.left) / rect.width  * 100).toFixed(4)
    const yPercent = ((event.clientY - rect.top)  / rect.height * 100).toFixed(4)

    if (this.hasXInputTarget)    this.xInputTarget.value    = xPercent
    if (this.hasYInputTarget)    this.yInputTarget.value    = yPercent
    if (this.hasPageInputTarget) this.pageInputTarget.value = this.currentPageValue

    this.#movePendingPin(xPercent, yPercent)
  }

  #movePendingPin(xPercent, yPercent) {
    if (!this.hasPendingPinTarget) return
    const pin = this.pendingPinTarget
    pin.style.left = `${xPercent}%`
    pin.style.top  = `${yPercent}%`
    // The drop belongs to the page it was made on — track it so paging away
    // hides the marker (it used to float over every page).
    pin.dataset.page = String(this.currentPageValue)
    pin.classList.remove("hidden")
  }

  // Show an already-saved pin position when re-opening the picker
  #showExistingPin() {
    if (!this.hasPendingPinTarget) return
    const pin = this.pendingPinTarget
    if (pin.dataset.hasPin === "true") {
      if (!pin.dataset.page && this.hasPageInputTarget) pin.dataset.page = this.pageInputTarget.value
      this.#syncPendingPinVisibility()
    }
  }

  // The pending/saved marker only shows on its own page.
  #syncPendingPinVisibility() {
    if (!this.hasPendingPinTarget) return
    const pin = this.pendingPinTarget
    if (pin.dataset.hasPin !== "true" && !pin.dataset.page) return

    const pinPage = parseInt(pin.dataset.page || "1", 10)
    pin.classList.toggle("hidden", pinPage !== this.currentPageValue)
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  #nextFrame() {
    return new Promise(resolve => requestAnimationFrame(resolve))
  }
}
